import { useMemo } from 'react';
import { PullRequestDetails, TestExecution } from '@finvari/deliver';
import { usePullRequests } from '../contexts/PullRequestsContext';
import { useTestExecutions } from '../contexts/TestExecutionsContext';
import { useReleaseState } from '../hooks/useReleaseState';
import { computePrWorkflow, PrWorkflow } from './prWorkflow';
import { filterUnreleased } from './releaseMembership';

export interface WorkflowData {
  // Every tracked PR (open + unreleased), keyed by PR number.
  workflowByPr: Map<number, PrWorkflow>;
  // Distinct author logins across open PRs, sorted, for the author filter.
  authors: string[];
  // Open PR count per author login, for the author filter's count badges.
  prCountByAuthor: Map<string, number>;
  openPrs: PullRequestDetails[];
  // The release backlog: merged into main, not yet on the release branch. What the Merged node holds.
  mergedPrs: PullRequestDetails[];
  loading: boolean;
  error: string | null;
}

export interface NodeCounts {
  // PR count per diagram node id.
  nodeCounts: Record<string, number>;
  // Count of priority-flagged PRs per node id (drives the red badge).
  priorityCounts: Record<string, number>;
}

// Tally how many PRs (and how many priority-flagged PRs) occupy each diagram node, over whatever set
// of PRs is passed in. The page calls this on the author-filtered set so the diagram counts always
// reflect the current author filter.
export function computeNodeCounts(
  prs: PullRequestDetails[],
  workflowByPr: Map<number, PrWorkflow>
): NodeCounts {
  const nodeCounts: Record<string, number> = {};
  const priorityCounts: Record<string, number> = {};
  for (const pr of prs) {
    const wf = workflowByPr.get(pr.pr?.number ?? 0);
    if (!wf) continue;
    for (const nodeId of wf.nodeIds) {
      nodeCounts[nodeId] = (nodeCounts[nodeId] ?? 0) + 1;
      if (wf.priority) priorityCounts[nodeId] = (priorityCounts[nodeId] ?? 0) + 1;
    }
  }
  return { nodeCounts, priorityCounts };
}

// Join the open + unreleased PRs with their test executions and derive each PR's workflow position,
// plus the per-node counts the diagram renders. All client-side and memoized.
export function useWorkflow(): WorkflowData {
  const { pullRequests, mergedPrs: allMergedPrs, loading: prsLoading, error: prsError } = usePullRequests();
  const { executionsByPr, loading: execLoading, error: execError } = useTestExecutions();
  const { releaseState, loading: releaseLoading, error: releaseError } = useReleaseState();

  // Exclude the synthetic main-branch pseudo-PR (id 0) from the workflow.
  const openPrs = useMemo(() => pullRequests.filter(pr => pr.pr?.id !== 0), [pullRequests]);
  // Merged is the release backlog, so a merged PR whose work has reached the release branch is done
  // and leaves the page entirely.
  const mergedPrs = useMemo(() => filterUnreleased(allMergedPrs, releaseState), [allMergedPrs, releaseState]);

  return useMemo(() => {
    const workflowByPr = new Map<number, PrWorkflow>();

    const derive = (pr: PullRequestDetails) => {
      const prNumber = pr.pr?.number ?? 0;
      const execs: TestExecution[] = executionsByPr.get(prNumber) ?? [];
      const wf = computePrWorkflow(pr, execs);
      workflowByPr.set(prNumber, wf);
    };

    openPrs.forEach(derive);
    mergedPrs.forEach(derive);

    const prCountByAuthor = new Map<string, number>();
    for (const pr of openPrs) {
      const login = pr.pr?.user?.login;
      if (!login) continue;
      prCountByAuthor.set(login, (prCountByAuthor.get(login) ?? 0) + 1);
    }
    const authors = Array.from(prCountByAuthor.keys()).sort((a, b) => a.localeCompare(b));

    return {
      workflowByPr,
      authors,
      prCountByAuthor,
      openPrs,
      mergedPrs,
      loading: prsLoading || execLoading || releaseLoading,
      error: prsError || execError || releaseError || null,
    };
  }, [openPrs, mergedPrs, executionsByPr, prsLoading, execLoading, releaseLoading, prsError, execError, releaseError]);
}
